// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import { useEffect } from 'react';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

/**
 * Custom hook for managing location persistence and coordinate enrichment
 * Extracted from NavigationPanel to reduce bundle size
 */
export const useLocationManager = ({
  currentUniverse,
  getCurrentLocations,
  getCurrentPositions,
  setCurrentLocation
}) => {
  const distancePositions = getCurrentPositions();

  // Helper function to ensure location always has universe + x/y/z coordinates
  const enrichLocationWithCoordinates = (location) => {
    if (!location) return null;
    
    // If already has coordinates, just ensure universeId
    if (location.x !== undefined && location.y !== undefined) {
      return {
        ...location,
        universeId: location.universeId || currentUniverse
      };
    }
    
    // Look up coordinates from distancePositions
    const coords = distancePositions[location.id];
    if (coords) {
      return {
        ...location,
        x: coords.x,
        y: coords.y,
        z: coords.z || 0,
        universeId: currentUniverse
      };
    }
    
    // Fallback: return location with universeId at least
    return {
      ...location,
      universeId: currentUniverse
    };
  };

  // Initialize and restore location when universe context is ready
  useEffect(() => {
    // Wait for universe context to be ready
    if (!currentUniverse) return;
    
    const saved = localStorage.getItem(STORAGE_KEYS.CURRENT_LOCATION);
    let locationToSet = null;
    
    if (saved) {
      try {
        const savedData = JSON.parse(saved);
        
        // Check if saved location is for current universe
        if (savedData.universeId === currentUniverse) {
          // Special case: stranded location with coordinates
          if (savedData.locationId === -1 && savedData.x !== undefined) {
            locationToSet = {
              id: -1,
              name: savedData.name || 'DEEP SPACE',
              threat: savedData.threat || 'UNKNOWN',
              resources: savedData.resources || 'NONE',
              x: savedData.x,
              y: savedData.y,
              z: savedData.z,
              universeId: savedData.universeId
            };
          } else {
            // Regular location: look up by ID in current universe
            const currentLocations = getCurrentLocations();
            const location = currentLocations.find(loc => loc.id === savedData.locationId);
            
            if (location) {
              locationToSet = enrichLocationWithCoordinates(location);
            }
          }
        }
      } catch (error) {
        console.error('[LocationManager] Failed to restore location:', error);
      }
    }
    
    // If no valid saved location for this universe, default to first location
    if (!locationToSet) {
      const currentLocations = getCurrentLocations();
      if (currentLocations.length > 0) {
        locationToSet = enrichLocationWithCoordinates(currentLocations[0]);
      }
    }
    
    if (locationToSet) {
      setCurrentLocation(locationToSet);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUniverse, getCurrentLocations, setCurrentLocation]);

  // Persist current location to localStorage whenever it changes
  const persistLocation = (currentLocation) => {
    // Only save if we have a valid location (not in-transit coordinates)
    if (currentLocation && !currentLocation.inTransit) {
      const saveData = {
        universeId: currentUniverse,
        locationId: currentLocation.id,
        // Legacy fields for backward compatibility
        id: currentLocation.id,
        name: currentLocation.name,
        distance: currentLocation.distance,
        threat: currentLocation.threat,
        resources: currentLocation.resources
      };
      
      // If stranded (id: -1), save coordinates too
      if (currentLocation.id === -1 && currentLocation.x !== undefined) {
        saveData.x = currentLocation.x;
        saveData.y = currentLocation.y;
        saveData.z = currentLocation.z;
      }
      
      localStorage.setItem(STORAGE_KEYS.CURRENT_LOCATION, JSON.stringify(saveData));
    }
  };

  return {
    enrichLocationWithCoordinates,
    persistLocation
  };
};

export default useLocationManager;